import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Plus, Package, DollarSign, TrendingUp, Edit2, Trash2, Video, 
  Upload, Copy, Check, Link, Users, MousePointer, ShoppingCart, Percent,
  Store, ExternalLink, Image as ImageIcon, X, CreditCard, AlertTriangle,
  CheckCircle, Loader2, FileText, BarChart3, AlertCircle, Tag, ArrowRight, Gift, Settings,
  RefreshCcw, Calendar, MessageSquare, Mail, Send, Rocket, ChevronDown, ChevronUp, Sparkles, Eye, Network
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { handleImageError } from '@/utils/imageUtils';
import ResilientImg from '@/components/ResilientImg';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VetScoreBadge } from '@/components/VetScoreBadge';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import CouponManager from '@/components/CouponManager';
import LinkAnalyticsTab from '@/components/LinkAnalyticsTab';
import CustomersTab from '@/components/CustomersTab';
import SubAffiliatesTab from '@/components/SubAffiliatesTab';
import InternationalPayoutsCard from '@/components/InternationalPayoutsCard';
import DeliveryConfigSection from '@/components/DeliveryConfigSection';
import LiveClickNotifier from '@/components/LiveClickNotifier';
import { getCart as getStoredCart, setCart as saveCart } from '@/utils/cartStorage';
import FeeRateCard from '@/components/FeeRateCard';
import FoundingCreatorShareCard from '@/components/FoundingCreatorShareCard';
import FeaturedPartnersStrip from '@/components/FeaturedPartnersStrip';
import CreatorAvatar from '@/components/CreatorAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Creator Onboarding Checklist Component
function CreatorOnboardingChecklist({ creator, stripeStatus, products, affiliatePrograms, upsells, onNavigateToTab }) {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('creator_onboarding_collapsed') === 'true';
  });
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem('creator_onboarding_dismissed') === 'true';
  });

  // Check completion status for each step
  const storeProfileComplete = creator?.store_name && creator?.bio;
  const stripeConnected = stripeStatus?.status === 'complete';
  // A creator who started Stripe onboarding but didn't finish has a
  // stripe_account_id but charges_enabled is still false. Surface this as
  // "Resume" so they know to pick up the SAME account, not start over.
  const stripeNeedsResume = !!stripeStatus?.stripe_account_id
    && !stripeStatus?.charges_enabled;
  const stripeBtnLabel = stripeConnected
    ? 'Stripe connected'
    : (stripeNeedsResume ? 'Resume Stripe setup' : 'Connect Stripe');
  const hasProducts = products?.length > 0;
  const hasAffiliateProgram = affiliatePrograms?.length > 0;
  const hasUpsellOrCouponOrSubscription = upsells?.length > 0 || products?.some(p => p.subscription_enabled);

  const steps = [
    {
      id: 1,
      title: 'Complete Your Store Profile',
      description: 'Add your store name, bio, and avatar to make your store stand out to buyers.',
      completed: storeProfileComplete,
      action: () => navigate(`/store/${creator?.store_slug}`),
      actionLabel: 'Edit Store',
      icon: Store
    },
    {
      id: 2,
      title: stripeNeedsResume ? 'Finish Stripe setup' : 'Connect Stripe',
      description: stripeNeedsResume
        ? "You've started a Stripe account but haven't finished the required steps yet. Click below to pick up where you left off — don't start a new account."
        : 'Connect your Stripe account to receive payments from customers. This is required to sell products.',
      completed: stripeConnected,
      action: null, // Handled by existing Stripe section
      actionLabel: stripeBtnLabel,
      icon: CreditCard
    },
    {
      id: 3,
      title: 'Create Your First Product',
      description: 'Upload your AI product with images, description, and delivery files. Set your pricing and start selling!',
      completed: hasProducts,
      action: () => onNavigateToTab('products'),
      actionLabel: 'Add Product',
      icon: Package
    },
    {
      id: 4,
      title: 'Set Up Affiliate Program',
      description: 'Let others promote your products and earn commissions. A great way to expand your reach!',
      completed: hasAffiliateProgram,
      action: () => onNavigateToTab('affiliates'),
      actionLabel: 'Create Program',
      icon: Users
    },
    {
      id: 5,
      title: 'Create Upsells, Coupons & Subscriptions',
      description: 'Boost your revenue with upsell funnels, discount coupons, and recurring subscription products.',
      completed: hasUpsellOrCouponOrSubscription,
      action: () => onNavigateToTab('upsells'),
      actionLabel: 'Get Started',
      icon: TrendingUp,
      recommended: true
    }
  ];

  const completedCount = steps.filter(s => s.completed).length;
  const allComplete = completedCount === steps.length;
  const progressPercent = (completedCount / steps.length) * 100;

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('creator_onboarding_collapsed', newState.toString());
  };

  const dismissChecklist = () => {
    setIsDismissed(true);
    localStorage.setItem('creator_onboarding_dismissed', 'true');
  };

  // Don't show if dismissed (user can show again by clearing localStorage)
  if (isDismissed) return null;

  return (
    <div className="bg-gradient-to-r from-white/10 via-[#00F0FF]/10 to-[#00F0FF]/10 border border-white/30 rounded-2xl mb-6 md:mb-8 overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 md:p-6 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
        onClick={toggleCollapse}
      >
        <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-[#00F0FF] to-white flex items-center justify-center flex-shrink-0">
            <Rocket className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base md:text-xl font-bold flex flex-wrap items-center gap-1 md:gap-2">
              <span>{allComplete ? '🎉 Complete!' : '🚀 Setup Store'}</span>
              {!allComplete && (
                <span className="text-xs md:text-sm font-normal text-neutral-400">
                  ({completedCount}/{steps.length})
                </span>
              )}
            </h2>
            <p className="text-neutral-400 text-xs md:text-sm truncate">
              {allComplete 
                ? 'Your store is ready!'
                : 'Complete steps to start selling'
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          {/* Progress Ring */}
          <div className="relative w-10 h-10 md:w-12 md:h-12 hidden sm:block">
            <svg className="w-10 h-10 md:w-12 md:h-12 transform -rotate-90">
              <circle
                cx="20"
                cy="20"
                r="16"
                stroke="currentColor"
                strokeWidth="3"
                fill="transparent"
                className="text-neutral-700 md:hidden"
              />
              <circle
                cx="20"
                cy="20"
                r="16"
                stroke="url(#progressGradient)"
                strokeWidth="3"
                fill="transparent"
                strokeDasharray={`${progressPercent * 1.005} 100.5`}
                strokeLinecap="round"
                className="md:hidden"
              />
              <circle
                cx="24"
                cy="24"
                r="20"
                stroke="currentColor"
                strokeWidth="4"
                fill="transparent"
                className="text-neutral-700 hidden md:block"
              />
              <circle
                cx="24"
                cy="24"
                r="20"
                stroke="url(#progressGradient)"
                strokeWidth="4"
                fill="transparent"
                strokeDasharray={`${progressPercent * 1.256} 125.6`}
                strokeLinecap="round"
                className="hidden md:block"
              />
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#00F0FF" />
                  <stop offset="100%" stopColor="#00F0FF" />
                </linearGradient>
              </defs>
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs md:text-sm font-bold">
              {completedCount}/{steps.length}
            </span>
          </div>
          {isCollapsed ? (
            <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-neutral-400" />
          ) : (
            <ChevronUp className="w-4 h-4 md:w-5 md:h-5 text-neutral-400" />
          )}
        </div>
      </div>

      {/* Steps List */}
      {!isCollapsed && (
        <div className="px-4 md:px-6 pb-4 md:pb-6">
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                  step.completed 
                    ? 'bg-green-500/10 border border-green-500/20' 
                    : step.locked
                    ? 'bg-neutral-900/30 border border-white/5 opacity-60'
                    : 'bg-neutral-900/50 border border-white/10 hover:border-[#00F0FF]/30'
                }`}
              >
                {/* Step Number / Check */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  step.completed 
                    ? 'bg-green-500 text-white' 
                    : step.locked
                    ? 'bg-neutral-800/50 text-neutral-500 border border-white/5'
                    : 'bg-neutral-800 text-neutral-400 border border-white/10'
                }`}>
                  {step.completed ? (
                    <Check className="w-5 h-5" />
                  ) : step.locked ? (
                    <AlertTriangle className="w-4 h-4" />
                  ) : (
                    <span className="font-bold">{step.id}</span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-semibold ${step.completed ? 'text-green-400' : step.locked ? 'text-neutral-500' : 'text-white'}`}>
                      {step.title}
                    </h3>
                    {step.required && !step.completed && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                        Required
                      </span>
                    )}
                    {step.recommended && !step.completed && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#00F0FF]/20 text-[#00F0FF]">
                        Recommended
                      </span>
                    )}
                    {step.locked && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
                        Verify ID First
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mt-1 ${step.locked ? 'text-neutral-500' : 'text-neutral-400'}`}>{step.description}</p>
                </div>

                {/* Action Button */}
                {!step.completed && step.action && !step.locked && (
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      step.action();
                    }}
                    className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full flex-shrink-0"
                  >
                    <step.icon className="w-4 h-4 mr-1" />
                    {step.actionLabel}
                  </Button>
                )}
                {step.completed && (
                  <span className="text-green-400 text-sm font-medium flex items-center gap-1 flex-shrink-0">
                    <CheckCircle className="w-4 h-4" />
                    Done
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Dismiss Button - Only show when all complete */}
          {allComplete && (
            <div className="mt-4 text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissChecklist}
                className="text-neutral-400 hover:text-white"
              >
                <X className="w-4 h-4 mr-1" />
                Dismiss Checklist
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreatorDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const productFileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [creator, setCreator] = useState(null);
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [categories, setCategories] = useState([]);
  const [affiliatePrograms, setAffiliatePrograms] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  // Derived: a creator who STARTED Stripe onboarding but never completed
  // all required steps has a stripe_account_id on file, but Stripe still
  // reports charges_enabled=false. Surface this as a "Resume" prompt so
  // they pick up the SAME account instead of accidentally creating a new
  // one (the duplicate-Stripe-account bug fixed in the previous session).
  // This MUST be derived at the parent-component scope because both the
  // onboarding checklist AND several pieces of the dashboard JSX
  // (yellow banner, Add Product button label, etc.) read this flag.
  const stripeNeedsResume = !!stripeStatus?.stripe_account_id
    && !stripeStatus?.charges_enabled;
  const [activeTab, setActiveTab] = useState('products');
  const [showProductModal, setShowProductModal] = useState(false);
  const [showAffiliateModal, setShowAffiliateModal] = useState(false);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [selectedProductForFiles, setSelectedProductForFiles] = useState(null);
  const [productFiles, setProductFiles] = useState([]);
  const [uploadingProductFile, setUploadingProductFile] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProductForAffiliate, setSelectedProductForAffiliate] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    features: '',
    images: '',
    video_demo_url: ''
  });
  const [affiliateFormData, setAffiliateFormData] = useState({
    commission_rate: 30,
    cookie_days: 30
  });
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [upsells, setUpsells] = useState([]);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [upsellFormData, setUpsellFormData] = useState({
    product_id: '',
    upsell_product_id: '',
    discount_percent: 10,
    headline: 'Special Offer!',
    description: '',
    position: 'checkout'
  });
  const [storefrontFormData, setStorefrontFormData] = useState({
    store_name: '',
    store_slug: '',
    bio: '',
    refund_policy: ''
  });
  const [savingStorefront, setSavingStorefront] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [isDraggingAvatar, setIsDraggingAvatar] = useState(false);
  const avatarFileInputRef = useRef(null);
  // Subscription states
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [enablingSubscription, setEnablingSubscription] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedProductForSubscription, setSelectedProductForSubscription] = useState(null);
  const [subscriptionFormData, setSubscriptionFormData] = useState({
    yearly_price: '',
    trial_days: 0
  });

  // Feedback state
  const [showFeedbackDropdown, setShowFeedbackDropdown] = useState(false);
  const [feedbackSubject, setFeedbackSubject] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [sendingFeedback, setSendingFeedback] = useState(false);

  // Identity verification state
  const [identityVerification, setIdentityVerification] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);

  const submitFeedback = async () => {
    if (!feedbackSubject.trim() || !feedbackMessage.trim()) {
      toast.error('Please fill in both subject and message');
      return;
    }
    
    setSendingFeedback(true);
    try {
      await axios.post(`${API_URL}/api/creator/feedback`, {
        subject: feedbackSubject,
        message: feedbackMessage
      });
      toast.success('Thank you for your feedback!');
      setFeedbackSubject('');
      setFeedbackMessage('');
      setShowFeedbackDropdown(false);
    } catch (error) {
      toast.error('Failed to send feedback');
    } finally {
      setSendingFeedback(false);
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      navigate('/');
      return;
    }
    const parsedUser = JSON.parse(storedUser);
    setUser(parsedUser);
    loadDashboardData(parsedUser);

    // Check for Stripe return parameters
    if (searchParams.get('stripe_connected') === 'true') {
      toast.success('Stripe account connected successfully!');
      checkStripeStatus();
    } else if (searchParams.get('stripe_refresh') === 'true') {
      toast.info('Please complete your Stripe onboarding');
      handleStripeOnboard();
    }
    
    // Check for identity verification return
    if (searchParams.get('verification') === 'complete') {
      toast.success('Identity verification submitted! We\'ll notify you when it\'s complete.');
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  const checkStripeStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await axios.get('/stripe/connect/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Error checking Stripe status:', error);
    }
  };

  const loadDashboardData = async (currentUser) => {
    try {
      const categoriesRes = await axios.get('/categories');
      setCategories(categoriesRes.data);

      // Get creator profile
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const creatorRes = await axios.get('/creators/me/profile', { headers });
        setCreator(creatorRes.data);
        
        // Initialize storefront form data from creator profile
        setStorefrontFormData({
          store_name: creatorRes.data.store_name || '',
          store_slug: creatorRes.data.store_slug || '',
          bio: creatorRes.data.bio || '',
          refund_policy: creatorRes.data.refund_policy || ''
        });

        // Get creator's products
        const productsRes = await axios.get('/my-products', { headers });
        setProducts(productsRes.data);

        // Get affiliate programs
        const affiliateRes = await axios.get('/affiliate-programs/my-products', { headers });
        setAffiliatePrograms(affiliateRes.data);

        // Get pending affiliate payouts
        try {
          const payoutsRes = await axios.get('/affiliate-payouts/pending', { headers });
          setPendingPayouts(payoutsRes.data);
          
          const historyRes = await axios.get('/affiliate-payouts/history', { headers });
          setPayoutHistory(historyRes.data);
        } catch {
          /* no payouts yet — non-fatal */
        }

        // Load upsells
        try {
          const upsellsRes = await axios.get('/my-upsells', { headers });
          setUpsells(upsellsRes.data.upsells || []);
        } catch {
          /* no upsells yet — non-fatal */
        }

        // Load subscription stats
        try {
          const subStatsRes = await axios.get('/subscriptions/creator-stats', { headers });
          setSubscriptionStats(subStatsRes.data);
        } catch {
          /* no subscription stats — non-fatal */
        }

        // Check Stripe status
        checkStripeStatus();

        // Check identity verification status
        checkIdentityVerification();

        // Calculate analytics
        const totalRevenue = productsRes.data.reduce((sum, p) => sum + (p.price * p.total_sales), 0);
        const totalOrders = productsRes.data.reduce((sum, p) => sum + p.total_sales, 0);
        
        setAnalytics({
          total_revenue: totalRevenue,
          total_orders: totalOrders,
          total_products: productsRes.data.length
        });
      } catch (error) {
        console.error('Error loading creator data:', error);
        if (error.response?.status === 404) {
          // User needs to set up creator profile - this happens when role is 'creator' but no profile yet
          toast.info('Setting up your creator profile...');
        }
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  };

  const handleStripeOnboard = async () => {
    setStripeLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post('/stripe/connect/onboard', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.onboarding_url) {
        // Redirect to Stripe onboarding
        window.location.href = response.data.onboarding_url;
      } else if (response.data.status === 'complete') {
        toast.success('Stripe account already connected!');
        setStripeStatus(response.data);
      }
    } catch (error) {
      console.error('Error starting Stripe onboarding:', error);
      toast.error(error.response?.data?.detail || 'Failed to start Stripe setup');
    } finally {
      setStripeLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get('/stripe/connect/dashboard-link', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  // Identity Verification Functions
  const checkIdentityVerification = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await axios.get('/identity/verification-status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIdentityVerification(response.data);
    } catch (err) {
      console.debug('Identity verification status not available (non-fatal):', err?.message || err);
    }
  };

  const handleIdentityVerification = async () => {
    setVerificationLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post('/identity/create-verification-session', 
        { frontend_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.url) {
        // Redirect to Stripe Identity verification
        window.location.href = response.data.url;
      } else if (response.data.status === 'verified') {
        toast.success('Identity already verified!');
        checkIdentityVerification();
      }
    } catch (error) {
      console.error('Error starting identity verification:', error);
      toast.error(error.response?.data?.detail || 'Failed to start identity verification');
    } finally {
      setVerificationLoading(false);
    }
  };

  const getStoreUrl = () => {
    if (!creator) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/store/${creator.store_slug}`;
  };

  const copyStoreLink = async () => {
    const url = getStoreUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      toast.success('Store link copied to clipboard!');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;

    setUploading(true);
    const token = localStorage.getItem('token');
    const headers = { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    };

    try {
      const formDataUpload = new FormData();
      for (let i = 0; i < files.length; i++) {
        formDataUpload.append('files', files[i]);
      }

      const response = await axios.post('/upload/images', formDataUpload, { headers });
      const newUrls = response.data.urls;
      
      setUploadedImages(prev => [...prev, ...newUrls]);
      
      // Also add to form data
      const currentImages = formData.images ? formData.images.split('\n').filter(i => i.trim()) : [];
      const allImages = [...currentImages, ...newUrls];
      setFormData(prev => ({ ...prev, images: allImages.join('\n') }));
      
      toast.success(`${newUrls.length} image(s) uploaded successfully!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeUploadedImage = (urlToRemove) => {
    setUploadedImages(prev => prev.filter(url => url !== urlToRemove));
    const currentImages = formData.images.split('\n').filter(i => i.trim() && i !== urlToRemove);
    setFormData(prev => ({ ...prev, images: currentImages.join('\n') }));
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        category_id: formData.category_id,
        features: formData.features.split('\n').filter(f => f.trim()),
        images: formData.images.split('\n').filter(i => i.trim()),
        video_demo_url: formData.video_demo_url || null
      };

      if (editingProduct) {
        await axios.put(`/products/${editingProduct.product_id}`, productData, { headers });
        toast.success('Product updated!');
      } else {
        await axios.post('/products', productData, { headers });
        toast.success('Product added successfully!');
      }

      setShowProductModal(false);
      resetForm();
      loadDashboardData(user);
    } catch (error) {
      console.error('Error saving product:', error);
      const detail = error.response?.data?.detail;
      // Trademark-risk-high response is a structured object with alt name suggestions
      if (detail && typeof detail === 'object' && detail.code === 'trademark_risk_high') {
        const alts = (detail.alt_name_suggestions || []).slice(0, 2).join(', ');
        toast.error(
          `${detail.message}${alts ? ` Try: ${alts}.` : ''}`,
          { duration: 8000, description: 'Please rename the product and resubmit.' }
        );
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to save product');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: '',
      features: '',
      images: '',
      video_demo_url: ''
    });
    setUploadedImages([]);
    setEditingProduct(null);
  };

  const openEditProduct = (product) => {
    // Same Stripe gate as Add — keep edit blocked until payouts work.
    if (!stripeStatus?.charges_enabled) {
      toast.error('Connect your Stripe account first. Open the yellow banner above and click Connect Stripe.');
      return;
    }
    // Populate the existing product-form modal with this product's current
    // values and open it in edit mode. The form submit handler already
    // branches on `editingProduct` → PUT vs POST, so the rest is reused.
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price != null ? String(product.price) : '',
      category_id: product.category_id || '',
      features: Array.isArray(product.features) ? product.features.join('\n') : '',
      images: Array.isArray(product.images) ? product.images.join('\n') : '',
      video_demo_url: product.video_demo_url || ''
    });
    setUploadedImages(Array.isArray(product.images) ? product.images : []);
    setShowProductModal(true);
  };

  const deleteProduct = async (product) => {
    if (!window.confirm(`Delete "${product.name}"?\n\nThis permanently removes the product and its delivery files. Historical orders and reviews will remain for reference. This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`/products/${product.product_id}`, { headers });
      toast.success(`"${product.name}" deleted`);
      loadDashboardData(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const handleCreateAffiliateProgram = async (e) => {
    e.preventDefault();
    if (!selectedProductForAffiliate) return;

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.post('/affiliate-programs', {
        product_id: selectedProductForAffiliate.product_id,
        commission_rate: parseFloat(affiliateFormData.commission_rate),
        cookie_days: parseInt(affiliateFormData.cookie_days)
      }, { headers });

      toast.success('Affiliate program created!');
      setShowAffiliateModal(false);
      setSelectedProductForAffiliate(null);
      loadDashboardData(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create affiliate program');
    }
  };

  const toggleAffiliateProgram = async (productId, isActive) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (isActive) {
        await axios.delete(`/affiliate-programs/${productId}`, { headers });
        toast.success('Affiliate program disabled');
      } else {
        // Re-enable with default values
        await axios.post('/affiliate-programs', {
          product_id: productId,
          commission_rate: 30,
          cookie_days: 30
        }, { headers });
        toast.success('Affiliate program enabled');
      }
      loadDashboardData(user);
    } catch (error) {
      toast.error('Failed to update affiliate program');
    }
  };

  // ============ AFFILIATE PAYOUTS ============

  const handleMarkPaid = async () => {
    if (!selectedPayout || !payoutAmount) {
      toast.error('Please enter the amount paid');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      await axios.post('/affiliate-payouts/mark-paid', {
        affiliate_link_id: selectedPayout.affiliate_link_id,
        amount: parseFloat(payoutAmount),
        notes: payoutNotes
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`Payout of $${payoutAmount} marked as paid`);
      setShowPayoutModal(false);
      setSelectedPayout(null);
      setPayoutAmount('');
      setPayoutNotes('');
      loadDashboardData(user);
    } catch (error) {
      toast.error('Failed to record payout');
    }
  };

  // ============ UPSELL MANAGEMENT ============

  const handleCreateUpsell = async () => {
    if (!upsellFormData.product_id || !upsellFormData.upsell_product_id) {
      toast.error('Please select both products');
      return;
    }
    if (upsellFormData.product_id === upsellFormData.upsell_product_id) {
      toast.error('Cannot upsell the same product');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      await axios.post(`/products/${upsellFormData.product_id}/upsells`, {
        upsell_product_id: upsellFormData.upsell_product_id,
        discount_percent: upsellFormData.discount_percent,
        headline: upsellFormData.headline,
        description: upsellFormData.description,
        position: upsellFormData.position
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Upsell created successfully!');
      setShowUpsellModal(false);
      setUpsellFormData({
        product_id: '',
        upsell_product_id: '',
        discount_percent: 10,
        headline: 'Special Offer!',
        description: '',
        position: 'checkout'
      });
      loadDashboardData(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create upsell');
    }
  };

  const handleDeleteUpsell = async (upsellId) => {
    if (!window.confirm('Delete this upsell?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`/upsells/${upsellId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Upsell deleted');
      loadDashboardData(user);
    } catch (error) {
      toast.error('Failed to delete upsell');
    }
  };

  // ============ SUBSCRIPTION MANAGEMENT ============

  const openSubscriptionModal = (product) => {
    setSelectedProductForSubscription(product);
    setSubscriptionFormData({
      yearly_price: product.yearly_price || (product.price * 10).toFixed(2),
      trial_days: product.trial_days || 0
    });
    setShowSubscriptionModal(true);
  };

  const handleEnableSubscription = async () => {
    if (!selectedProductForSubscription) return;
    
    const token = localStorage.getItem('token');
    setEnablingSubscription(selectedProductForSubscription.product_id);
    
    try {
      await axios.post('/subscriptions/create-product', {
        product_id: selectedProductForSubscription.product_id,
        yearly_price: parseFloat(subscriptionFormData.yearly_price),
        trial_days: parseInt(subscriptionFormData.trial_days) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Subscription enabled for this product!');
      setShowSubscriptionModal(false);
      setSelectedProductForSubscription(null);
      loadDashboardData(user);
    } catch (error) {
      console.error('Error enabling subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to enable subscription');
    } finally {
      setEnablingSubscription(null);
    }
  };

  const handleDisableSubscription = async (productId) => {
    if (!window.confirm('Disable subscriptions for this product? Existing subscribers will not be affected.')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.post(`/products/${productId}/disable-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscription disabled');
      loadDashboardData(user);
    } catch (error) {
      toast.error('Failed to disable subscription');
    }
  };

  // ============ PRODUCT FILE MANAGEMENT ============
  
  const openFilesModal = async (product) => {
    setSelectedProductForFiles(product);
    setShowFilesModal(true);
    await loadProductFiles(product.product_id);
  };

  const loadProductFiles = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/products/${productId}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductFiles(response.data);
    } catch (error) {
      console.error('Error loading product files:', error);
      setProductFiles([]);
    }
  };

  const handleProductFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedProductForFiles) return;

    // Validate file size (50MB max)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 50MB.');
      return;
    }

    setUploadingProductFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/products/${selectedProductForFiles.product_id}/upload-file`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('File uploaded successfully!');
      await loadProductFiles(selectedProductForFiles.product_id);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingProductFile(false);
      if (productFileInputRef.current) {
        productFileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteProductFile = async (fileId) => {
    if (!selectedProductForFiles) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/products/${selectedProductForFiles.product_id}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted');
      await loadProductFiles(selectedProductForFiles.product_id);
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getAffiliateLink = (product) => {
    const program = affiliatePrograms.find(p => p.product_id === product.product_id);
    if (!program) return null;
    return `${window.location.origin}/product/${product.product_id}?ref=YOUR_CODE`;
  };

  const handleSaveStorefront = async (e) => {
    e.preventDefault();
    
    if (!storefrontFormData.refund_policy?.trim()) {
      toast.error('Refund policy is required');
      return;
    }
    
    setSavingStorefront(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.put('/creators/me/profile', {
        store_name: storefrontFormData.store_name,
        store_slug: storefrontFormData.store_slug,
        bio: storefrontFormData.bio,
        refund_policy: storefrontFormData.refund_policy,
        avatar: creator?.avatar || null,
        banner: creator?.banner || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreator(response.data);
      toast.success('Storefront settings saved successfully!');
    } catch (error) {
      console.error('Error saving storefront:', error);
      toast.error(error.response?.data?.detail || 'Failed to save storefront settings');
    } finally {
      setSavingStorefront(false);
    }
  };

  const _processAvatarFile = async (file) => {
    if (!file) return;

    // Client-side guardrails (server also enforces 5MB + extension)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Use a JPG, PNG, WEBP, or GIF image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller.');
      return;
    }

    setUploadingAvatar(true);
    const token = localStorage.getItem('token');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const uploadRes = await axios.post('/upload/image', fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const avatarUrl = uploadRes.data?.url;
      if (!avatarUrl) throw new Error('Upload returned no URL');

      const profileRes = await axios.put('/creators/me/profile', {
        avatar: avatarUrl,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCreator(profileRes.data);
      toast.success('Avatar updated!');
    } catch (err) {
      console.error('Avatar upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload avatar. Please try again.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarFilePicked = async (e) => {
    const file = e.target.files?.[0];
    await _processAvatarFile(file);
    if (avatarFileInputRef.current) avatarFileInputRef.current.value = '';
  };

  const handleAvatarDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAvatar(false);
    if (uploadingAvatar) return;
    const file = e.dataTransfer?.files?.[0];
    await _processAvatarFile(file);
  };

  const handleAvatarDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploadingAvatar) setIsDraggingAvatar(true);
  };

  const handleAvatarDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAvatar(false);
  };

  const handleRemoveAvatar = async () => {
    setUploadingAvatar(true);
    const token = localStorage.getItem('token');
    try {
      const profileRes = await axios.put('/creators/me/profile', {
        avatar: '',
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCreator(profileRes.data);
      toast.success('Avatar removed. Using your auto-generated avatar.');
    } catch (err) {
      console.error('Avatar remove failed:', err);
      toast.error('Failed to remove avatar.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (!user || (user.role !== 'creator' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">{t('dashboard.creatorAccessRequired')}</h1>
          <p className="text-neutral-400 mb-6">{t('dashboard.needCreatorAccount')}</p>
          <Button onClick={() => navigate('/')} className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black">
            {t('product.backToMarketplace')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <LiveClickNotifier />
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-3 md:px-12 py-3 md:py-4 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#00F0FF] hover:text-[#00C2CC] px-2 md:px-4 flex-shrink-0"
            data-testid="back-to-marketplace"
          >
            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 md:mr-2" />
            <span className="hidden md:inline">{t('product.backToMarketplace')}</span>
          </Button>
          <h1 className="text-base md:text-2xl font-bold truncate">{t('dashboard.title')}</h1>

          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            {/* Cart */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/checkout')}
              className="relative h-9 w-9"
              data-testid="dashboard-cart-button"
              aria-label="Cart"
            >
              <ShoppingCart className="w-[18px] h-[18px]" />
            </Button>
            {/* Messages */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/messages')}
              className="relative h-9 w-9"
              data-testid="dashboard-messages-button"
              aria-label="Messages"
            >
              <Mail className="w-[18px] h-[18px]" />
            </Button>
            {/* Chat Rooms — Deals Room, Creators Lounge, etc. */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/chat')}
              className="relative h-9 w-9 hidden sm:inline-flex"
              data-testid="dashboard-chat-button"
              aria-label="Chat rooms"
              title="Chat rooms · Deals Room, Creators Lounge"
            >
              <Users className="w-[18px] h-[18px]" />
            </Button>

            {/* Feedback Button */}
            <div className="relative">
              <Button
                onClick={() => setShowFeedbackDropdown(!showFeedbackDropdown)}
                variant="outline"
                size="sm"
                className="border-[#00FF94]/30 text-[#00FF94] hover:bg-[#00FF94]/10 px-2 md:px-4"
              >
                <MessageSquare className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Feedback</span>
              </Button>
            
            {/* Feedback Dropdown */}
            {showFeedbackDropdown && (
              <div className="absolute right-0 top-12 w-80 bg-neutral-900 border border-white/10 rounded-xl shadow-xl z-50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-[#00FF94]">Send Feedback</h3>
                  <button 
                    onClick={() => setShowFeedbackDropdown(false)}
                    className="text-neutral-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-neutral-400 mb-4">
                  Send your feedback to Admin - what can we improve?
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1">Subject</label>
                    <Input
                      value={feedbackSubject}
                      onChange={(e) => setFeedbackSubject(e.target.value)}
                      placeholder="e.g., Feature request, Bug report..."
                      className="bg-neutral-800 border-white/10 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1">Message</label>
                    <Textarea
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      placeholder="Tell us what you think..."
                      className="bg-neutral-800 border-white/10 text-sm"
                      rows={4}
                    />
                  </div>
                  <Button
                    onClick={submitFeedback}
                    disabled={sendingFeedback || !feedbackSubject.trim() || !feedbackMessage.trim()}
                    className="w-full bg-[#00FF94] hover:bg-[#00D67A] text-black"
                  >
                    {sendingFeedback ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Feedback
                  </Button>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 md:px-12 py-6 md:py-12">
        {/* Creator Onboarding Checklist */}
        {creator && (
          <CreatorOnboardingChecklist 
            creator={creator}
            stripeStatus={stripeStatus}
            products={products}
            affiliatePrograms={affiliatePrograms}
            upsells={upsells}
            onNavigateToTab={(tab) => setActiveTab(tab)}
          />
        )}

        {/* Pre-Launch Test Mode Banner */}
        {(user?.vip_creator || user?.role === 'creator' || user?.role === 'admin') && (
          <div className="bg-gradient-to-r from-[#00F0FF]/20 to-[#00FF94]/20 border border-[#00F0FF]/30 rounded-2xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/20 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#00F0FF] flex items-center gap-2">
                    Pre-Launch Test Mode Available
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#00F0FF]/20 text-[#00F0FF]">VIP Perk</span>
                  </h2>
                  <p className="text-neutral-400 text-sm mt-1">
                    {products?.length > 0 
                      ? 'Test purchase your own products without real charges! Add to cart and select "Test Mode" at checkout.'
                      : 'Once you add products, you can test the full buyer experience without real charges!'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate('/marketplace-preview')}
                  variant="outline"
                  className="border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-xl"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Marketplace
                </Button>
                {products?.length > 0 && (
                  <Button
                    onClick={() => {
                      // Add first product to cart for testing
                      const product = products[0];
                      const cart = getStoredCart();
                      const exists = cart.find(item => item.product_id === product.product_id);
                      if (!exists) {
                        cart.push({
                          product_id: product.product_id,
                          name: product.name,
                          price: product.price,
                          image: product.images?.[0],
                          quantity: 1,
                          creator_id: product.creator_id
                        });
                        saveCart(cart);
                        window.dispatchEvent(new Event('cartUpdated'));
                        toast.success(`Added "${product.name}" to cart for testing!`);
                      }
                      navigate('/checkout');
                    }}
                    className="bg-[#00F0FF] hover:bg-[#FFB800] text-black rounded-xl font-bold"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Test Buy Now
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Store Link Section */}
        {creator && (
          <div className="bg-gradient-to-r from-[#00F0FF]/10 via-white/10 to-[#00F0FF]/10 border border-white/10 rounded-2xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/20 flex items-center justify-center">
                  <Store className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{creator.store_name}</h2>
                  <p className="text-neutral-400 text-sm">{t('dashboard.shareStoreLink')}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
                <div className="flex-1 bg-neutral-900 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-2 min-w-0">
                  <Link className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                  <span className="text-sm text-neutral-300 truncate" data-testid="store-url">
                    {getStoreUrl()}
                  </span>
                </div>
                <Button
                  onClick={copyStoreLink}
                  className={`${copiedLink ? 'bg-green-500' : 'bg-[#00F0FF]'} hover:bg-[#00C2CC] text-black rounded-xl font-bold px-6`}
                  data-testid="copy-store-link"
                >
                  {copiedLink ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copiedLink ? 'Copied!' : 'Copy Link'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(getStoreUrl(), '_blank')}
                  className="border-white/10 hover:bg-white/5 rounded-xl"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Visit Store
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/marketplace-preview')}
                  className="border-white/30 text-white hover:bg-[#00F0FF]/10 hover:text-[#00F0FF] hover:border-[#00F0FF]/50 rounded-xl"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Marketplace
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stripe Connect Section - Payment Setup - Show for all creators */}
        {/* Identity Verification Required Section - Show BEFORE Stripe if not verified */}
        {/* Stripe Connect Section - Show if not connected */}
        {user?.role === 'creator' && (!stripeStatus || stripeStatus.status !== 'complete') && (
          <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-2xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-orange-400">Step 2: {t('dashboard.setUpPayments')}</h2>
                  <p className="text-neutral-400 text-sm">{t('dashboard.connectStripeToReceive')}</p>
                </div>
              </div>
              <Button
                onClick={handleStripeOnboard}
                disabled={stripeLoading}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold px-6"
              >
                {stripeLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    {t('dashboard.stripeConnect')}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Stripe Connected Success Banner */}
        {stripeStatus && stripeStatus.status === 'complete' && (
          <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-2xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-green-400">{t('dashboard.paymentsReady')}</h2>
                  <p className="text-neutral-400 text-sm">{t('dashboard.stripeConnectedReceive')}</p>
                </div>
              </div>
              <Button
                onClick={openStripeDashboard}
                variant="outline"
                className="border-green-500/30 text-green-400 hover:bg-green-500/10 rounded-xl"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('dashboard.viewStripeDashboard')}
              </Button>
            </div>
          </div>
        )}

        {/* Founding Creator share module — only renders for the first 100 */}
        {creator?.is_founding_creator && creator?.founding_creator_number && (
          <FoundingCreatorShareCard
            number={creator.founding_creator_number}
            storeSlug={creator.store_slug}
          />
        )}

        {/* Current fee rate banner — sourced from /api/me/fee */}
        <FeeRateCard compact={false} className="mb-6 md:mb-8" />

        {/* Featured partners strip — social proof (Marblism + more) */}
        <FeaturedPartnersStrip />

        {/* Analytics Cards - Horizontal scroll on mobile */}
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide mb-8 md:mb-12">
          <div className="flex md:grid md:grid-cols-3 gap-4 md:gap-6 min-w-max md:min-w-0">
            <div className="bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 border border-[#00F0FF]/30 rounded-2xl p-4 md:p-6 w-[180px] md:w-auto flex-shrink-0 md:flex-shrink">
              <div className="flex items-center gap-2 md:gap-3 mb-2">
                <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-[#00F0FF]" />
                <h3 className="text-neutral-400 text-xs md:text-sm font-semibold">{t('dashboard.totalRevenue')}</h3>
              </div>
              <p className="text-2xl md:text-4xl font-black text-[#00F0FF]" data-testid="total-revenue">
                ${analytics?.total_revenue?.toFixed(2) || '0.00'}
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/20 to-white/5 border border-white/30 rounded-2xl p-4 md:p-6 w-[180px] md:w-auto flex-shrink-0 md:flex-shrink">
              <div className="flex items-center gap-2 md:gap-3 mb-2">
                <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-white" />
                <h3 className="text-neutral-400 text-xs md:text-sm font-semibold">{t('dashboard.totalOrders')}</h3>
              </div>
              <p className="text-2xl md:text-4xl font-black text-white" data-testid="total-orders">
                {analytics?.total_orders || 0}
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 border border-[#00F0FF]/30 rounded-2xl p-4 md:p-6 w-[180px] md:w-auto flex-shrink-0 md:flex-shrink">
              <div className="flex items-center gap-2 md:gap-3 mb-2">
                <Package className="w-5 h-5 md:w-6 md:h-6 text-[#00F0FF]" />
                <h3 className="text-neutral-400 text-xs md:text-sm font-semibold">{t('dashboard.totalProducts')}</h3>
              </div>
              <p className="text-2xl md:text-4xl font-black text-[#00F0FF]" data-testid="total-products">
                {analytics?.total_products || 0}
              </p>
            </div>
          </div>
        </div>

        {/* International payouts onboarding (renders only for creators in
            countries Stripe Connect doesn't support) */}
        <div className="mb-6">
          <InternationalPayoutsCard />
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Horizontal scrollable tabs on mobile */}
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide mb-6 md:mb-8">
            <TabsList className="bg-neutral-900 border border-white/10 rounded-xl p-1 flex md:inline-flex min-w-max md:min-w-0">
              <TabsTrigger 
                value="products" 
                className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black rounded-lg px-4 md:px-6 whitespace-nowrap"
              >
                <Package className="w-4 h-4 mr-1 md:mr-2" />
                <span className="text-sm">Products</span>
              </TabsTrigger>
              <TabsTrigger 
                value="upsells" 
                className="data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-black rounded-lg px-4 md:px-6 whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-1 md:mr-2" />
                <span className="text-sm">Upsells</span>
              </TabsTrigger>
              <TabsTrigger 
                value="affiliates" 
                className="data-[state=active]:bg-white data-[state=active]:text-black rounded-lg px-4 md:px-6 whitespace-nowrap"
              >
                <Users className="w-4 h-4 mr-1 md:mr-2" />
                <span className="text-sm">Affiliates</span>
              </TabsTrigger>
              <TabsTrigger 
                value="coupons" 
                className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black rounded-lg px-4 md:px-6 whitespace-nowrap"
              >
                <Tag className="w-4 h-4 mr-1 md:mr-2" />
                <span className="text-sm">Coupons</span>
              </TabsTrigger>
              <TabsTrigger 
                value="storefront" 
                className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black rounded-lg px-4 md:px-6 whitespace-nowrap"
              >
                <Settings className="w-4 h-4 mr-1 md:mr-2" />
                <span className="text-sm">Storefront</span>
              </TabsTrigger>
              <TabsTrigger 
                value="subscriptions" 
                className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black rounded-lg px-4 md:px-6 whitespace-nowrap"
              >
                <RefreshCcw className="w-4 h-4 mr-1 md:mr-2" />
                <span className="text-sm">Subs</span>
              </TabsTrigger>
              <TabsTrigger 
                value="link-analytics" 
                className="data-[state=active]:bg-[#1DA1F2] data-[state=active]:text-black rounded-lg px-4 md:px-6 whitespace-nowrap"
              >
                <BarChart3 className="w-4 h-4 mr-1 md:mr-2" />
                <span className="text-sm">Link Analytics</span>
              </TabsTrigger>
              <TabsTrigger
                value="customers"
                data-testid="customers-tab-trigger"
                className="data-[state=active]:bg-[#FFB400] data-[state=active]:text-black rounded-lg px-4 md:px-6 whitespace-nowrap"
              >
                <Users className="w-4 h-4 mr-1 md:mr-2" />
                <span className="text-sm">Customers</span>
              </TabsTrigger>
              <TabsTrigger
                value="sub-affiliates"
                data-testid="sub-affiliates-tab-trigger"
                className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black rounded-lg px-4 md:px-6 whitespace-nowrap"
              >
                <Network className="w-4 h-4 mr-1 md:mr-2" />
                <span className="text-sm">Sub-Affiliates</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Quick Links Row - Horizontal scroll on mobile */}
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide mb-6 md:mb-8">
            <div className="flex gap-3 md:gap-4 min-w-max md:min-w-0">
              <Button
                variant="outline"
                onClick={() => navigate('/seller/analytics')}
                className="border-white/20 hover:bg-white/5 whitespace-nowrap"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                View Analytics
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/seller/disputes')}
                className="border-white/20 hover:bg-white/5 whitespace-nowrap"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Customer Disputes
              </Button>
            </div>
          </div>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your Products
              </h2>
              {stripeStatus?.charges_enabled ? (
              <Dialog open={showProductModal} onOpenChange={(open) => { setShowProductModal(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => resetForm()}
                    className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full font-bold"
                    data-testid="add-product-button"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold">
                      {editingProduct ? 'Edit Product' : 'Add New Product'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-6 mt-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Product Name*</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., AI Content Generator Pro"
                        className="bg-neutral-800 border-white/10"
                        required
                        data-testid="product-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Category*</label>
                      <select
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        className="w-full bg-neutral-800 border border-white/10 rounded-lg px-4 py-3 text-white"
                        required
                        data-testid="category-select"
                      >
                        <option value="">Select a category</option>
                        {categories.map(cat => (
                          <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Description*</label>
                      <Textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Describe what your product does and who it's for..."
                        className="bg-neutral-800 border-white/10 min-h-[100px]"
                        required
                        data-testid="description-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Price (USD)*</label>
                      <Input
                        name="price"
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={handleInputChange}
                        placeholder="49.99"
                        className="bg-neutral-800 border-white/10"
                        required
                        data-testid="price-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Features* <span className="text-neutral-500 font-normal">(one per line)</span>
                      </label>
                      <Textarea
                        name="features"
                        value={formData.features}
                        onChange={handleInputChange}
                        placeholder="AI-powered content generation&#10;50+ templates&#10;SEO optimization&#10;Export to multiple formats"
                        className="bg-neutral-800 border-white/10 min-h-[120px]"
                        required
                        data-testid="features-input"
                      />
                    </div>

                    {/* Image Upload Section */}
                    <div>
                      <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-[#00F0FF]" />
                        Product Images*
                      </label>
                      
                      {/* Upload Button */}
                      <div className="mb-4">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          accept="image/*"
                          multiple
                          className="hidden"
                          data-testid="image-upload-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="border-dashed border-2 border-white/20 hover:border-[#00F0FF]/50 bg-transparent w-full py-8 rounded-xl"
                        >
                          <Upload className={`w-6 h-6 mr-2 ${uploading ? 'animate-pulse' : ''}`} />
                          {uploading ? 'Uploading...' : 'Click to upload images from your device'}
                        </Button>
                      </div>

                      {/* Uploaded Images Preview */}
                      {uploadedImages.length > 0 && (
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          {uploadedImages.map((url, index) => (
                            <div key={`upl-${url}`} className="relative group aspect-video bg-neutral-800 rounded-lg overflow-hidden">
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}${url}`} 
                                alt={`Upload ${index + 1}`} 
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeUploadedImage(url)}
                                className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* URL Input (optional) */}
                      <Textarea
                        name="images"
                        value={formData.images}
                        onChange={handleInputChange}
                        placeholder="Or paste image URLs (one per line)&#10;https://example.com/image1.jpg"
                        className="bg-neutral-800 border-white/10 min-h-[60px]"
                        data-testid="images-input"
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Upload images directly or paste URLs from Imgur, Unsplash, etc.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                        <Video className="w-4 h-4 text-white" />
                        Video Demo URL <span className="text-neutral-500 font-normal">(optional)</span>
                      </label>
                      <Input
                        name="video_demo_url"
                        value={formData.video_demo_url}
                        onChange={handleInputChange}
                        placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                        className="bg-neutral-800 border-white/10"
                        data-testid="video-url-input"
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Supports YouTube, Vimeo, or direct MP4 links. Show your product in action!
                      </p>
                    </div>

                    <div className="flex gap-4 pt-4">
                      <Button
                        type="submit"
                        className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full font-bold"
                        data-testid="submit-product-button"
                      >
                        {editingProduct ? 'Update Product' : 'Create Product'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowProductModal(false);
                          resetForm();
                        }}
                        className="border-white/10 hover:bg-white/5 rounded-full"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              ) : (
                <Button
                  onClick={() => {
                    const sub = document.querySelector('[data-testid="connect-stripe-banner-btn"]');
                    if (sub) sub.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  className="bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-300 border border-yellow-500/40 rounded-full font-bold"
                  data-testid="add-product-button-stripe-required"
                >
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  {stripeNeedsResume ? 'Finish Stripe to Add Products' : 'Connect Stripe to Add Products'}
                </Button>
              )}
            </div>

            {/* Stripe gate banner — visible above products list when not connected.
                Tells the creator exactly why they can't upload yet, and how to fix it. */}
            {!stripeStatus?.charges_enabled && (
              <div
                className="mb-6 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-4 md:p-5 flex items-start gap-3"
                data-testid="products-stripe-gate-banner"
              >
                <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-yellow-400 text-sm md:text-base mb-1">
                    {stripeNeedsResume ? 'Finish your Stripe setup' : 'Connect Stripe before uploading products'}
                  </h4>
                  <p className="text-xs md:text-sm text-neutral-300 leading-relaxed">
                    {stripeNeedsResume
                      ? "You started Stripe onboarding but didn't complete every required step yet. Click below to pick up where you left off on the SAME account — don't create a new one."
                      : 'Buyers pay through Stripe — without it connected we can\u2019t pay you out, so product uploads are paused. Stripe\u2019s onboarding takes ~3 minutes. After that, your products list and start selling instantly.'}
                  </p>
                  <Button
                    onClick={handleStripeOnboard}
                    size="sm"
                    className="mt-3 bg-yellow-500 hover:bg-yellow-600 text-black text-sm font-bold rounded-full"
                    data-testid="connect-stripe-banner-btn"
                  >
                    {stripeNeedsResume ? 'Resume Stripe setup' : 'Connect Stripe Now'}
                  </Button>
                </div>
              </div>
            )}

            {/* Products Grid - Horizontal scroll on mobile */}
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide">
              <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 min-w-max md:min-w-0">
              {products.length === 0 ? (
                <div className="w-full md:col-span-full text-center py-20">
                  <Package className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">No products yet</h3>
                  <p className="text-neutral-400 mb-6">Start by adding your first AI product</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <Button
                      onClick={() => {
                        if (!stripeStatus?.charges_enabled) {
                          toast.error('Connect your Stripe account first. Open the yellow banner above and click Connect Stripe.');
                          return;
                        }
                        setShowProductModal(true);
                      }}
                      className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Add Your First Product
                    </Button>
                    <Button
                      data-testid="api-quickstart-cta"
                      onClick={() => navigate('/dashboard/quickstart/api')}
                      variant="outline"
                      className="rounded-full border-[#00F0FF]/40 bg-[#00F0FF]/5 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Selling an API or SaaS? Quick-start →
                    </Button>
                  </div>
                </div>
              ) : (
                products.map(product => (
                  <div
                    key={product.product_id}
                    className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden hover:border-[#00F0FF]/30 transition-all cursor-pointer group w-[280px] md:w-auto flex-shrink-0 md:flex-shrink"
                    data-testid={`product-card-${product.product_id}`}
                    onClick={() => navigate(`/product/${product.product_id}`)}
                  >
                    <div className="aspect-video bg-neutral-800 relative">
                      <ResilientImg 
                        src={product.images[0]?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${product.images[0]}` : product.images[0]} 
                        alt={product.name} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {product.video_demo_url && (
                        <div className="absolute top-3 right-3 bg-[#00F0FF] text-black px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Video className="w-3 h-3" />
                          DEMO
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="font-bold text-lg mb-2 group-hover:text-[#00F0FF] transition-colors">{product.name}</h3>
                      <p className="text-neutral-400 text-sm mb-4 line-clamp-2">{product.description}</p>
                      <VetScoreBadge vetting={product.vetting_results} />
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl font-bold text-[#00F0FF]">${product.price}</span>
                        <span className="text-sm text-neutral-500">{product.total_sales} sales</span>
                      </div>
                      
                      {/* File delivery indicator */}
                      <div className="flex items-center gap-2 mb-4 text-sm">
                        {product.delivery_files?.length > 0 ? (
                          <span className="flex items-center gap-1 text-green-500">
                            <CheckCircle className="w-4 h-4" />
                            {product.delivery_files.length} file(s) attached
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-yellow-500">
                            <AlertTriangle className="w-4 h-4" />
                            No delivery files
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 min-w-[60px] border-white/10 hover:bg-white/5 rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/product/${product.product_id}`);
                          }}
                          data-testid={`view-product-${product.product_id}`}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#00F0FF]/30 hover:bg-[#00F0FF]/10 text-[#00F0FF] rounded-full px-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditProduct(product);
                          }}
                          title="Edit product"
                          data-testid={`edit-product-${product.product_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/30 hover:bg-red-500/10 text-red-400 rounded-full px-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteProduct(product);
                          }}
                          title="Delete product"
                          data-testid={`delete-product-${product.product_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#00F0FF]/20 hover:bg-[#00F0FF]/10 text-[#00F0FF] rounded-full px-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = `https://theailedger.ai/product/${product.slug || product.product_id}`;
                            try { navigator.clipboard.writeText(url); } catch(err) { const t = document.createElement('textarea'); t.value = url; document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t); }
                            toast.success(`Link copied for "${product.name}"`);
                          }}
                          title="Copy product link"
                          data-testid={`copy-link-${product.product_id}`}
                        >
                          <Link className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openFilesModal(product);
                          }}
                          className="flex-1 min-w-[80px] bg-white hover:bg-neutral-200 text-black rounded-full"
                          data-testid={`files-product-${product.product_id}`}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Files
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
              </div>
            </div>
          </TabsContent>

          {/* Upsells Tab */}
          <TabsContent value="upsells">
            <div className="space-y-8">
              {/* Upsells Overview */}
              <div className="bg-gradient-to-r from-[#FF6B6B]/10 to-[#00F0FF]/10 border border-white/10 rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <TrendingUp className="w-8 h-8 text-[#FF6B6B]" />
                      Upsell Funnels
                    </h2>
                    <p className="text-neutral-400 mt-2">
                      Increase your average order value by offering complementary products at checkout.
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowUpsellModal(true)}
                    className="bg-[#FF6B6B] hover:bg-[#FF5252] text-white"
                    disabled={products.length < 2}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Upsell
                  </Button>
                </div>

                {products.length < 2 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                    <p className="text-yellow-400 text-sm">
                      You need at least 2 products to create upsells. Create more products first!
                    </p>
                  </div>
                )}

                {/* How It Works */}
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-neutral-900/50 rounded-xl p-4">
                    <div className="w-8 h-8 rounded-full bg-[#FF6B6B]/20 flex items-center justify-center mb-2">
                      <span className="text-[#FF6B6B] font-bold">1</span>
                    </div>
                    <h4 className="font-medium mb-1">Select Trigger Product</h4>
                    <p className="text-xs text-neutral-400">Choose which product triggers the upsell</p>
                  </div>
                  <div className="bg-neutral-900/50 rounded-xl p-4">
                    <div className="w-8 h-8 rounded-full bg-[#00F0FF]/20 flex items-center justify-center mb-2">
                      <span className="text-[#00F0FF] font-bold">2</span>
                    </div>
                    <h4 className="font-medium mb-1">Choose Upsell Product</h4>
                    <p className="text-xs text-neutral-400">Select the product to offer as an upsell</p>
                  </div>
                  <div className="bg-neutral-900/50 rounded-xl p-4">
                    <div className="w-8 h-8 rounded-full bg-[#00F0FF]/20 flex items-center justify-center mb-2">
                      <span className="text-[#00F0FF] font-bold">3</span>
                    </div>
                    <h4 className="font-medium mb-1">Set Discount</h4>
                    <p className="text-xs text-neutral-400">Optional discount to incentivize the purchase</p>
                  </div>
                </div>
              </div>

              {/* Upsells List */}
              <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
                <h3 className="font-bold mb-4">Your Upsells ({upsells.length})</h3>
                
                {upsells.length === 0 ? (
                  <div className="text-center py-12">
                    <TrendingUp className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                    <p className="text-neutral-400">No upsells created yet</p>
                    <p className="text-sm text-neutral-500 mt-1">Create your first upsell to boost sales!</p>
                  </div>
                ) : (
                  <div className="space-y-3 md:space-y-4">
                    {upsells.map((upsell) => (
                      <div
                        key={upsell.upsell_id}
                        className="bg-neutral-800/50 border border-white/10 rounded-xl p-3 md:p-4"
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
                            <div className="text-center min-w-0 flex-1">
                              <p className="text-xs text-neutral-500 mb-1">When buying</p>
                              <p className="font-medium text-[#00F0FF] text-sm truncate">{upsell.product_name}</p>
                            </div>
                            <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-neutral-500 flex-shrink-0" />
                            <div className="text-center min-w-0">
                              <p className="text-xs text-neutral-500 mb-1">Offer</p>
                              <p className="font-medium text-[#00F0FF] text-sm truncate">{upsell.upsell_product_name}</p>
                              {upsell.discount_percent > 0 && (
                                <span className="text-xs px-1.5 py-0.5 bg-[#FF6B6B]/20 text-[#FF6B6B] rounded-full mt-1 inline-block">
                                  -{upsell.discount_percent}%
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                            <span className="text-xs text-neutral-500 px-2 py-1 bg-neutral-800 rounded">
                              {upsell.position}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteUpsell(upsell.upsell_id)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10 p-1.5 md:p-2"
                            >
                              <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-neutral-400 mt-2">{upsell.headline}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Affiliate Program Tab */}
          <TabsContent value="affiliates">
            <div className="space-y-8">
              {/* Affiliate Overview */}
              <div className="bg-gradient-to-r from-white/10 to-[#00F0FF]/10 border border-white/10 rounded-2xl p-8">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                  <Users className="w-8 h-8 text-white" />
                  Affiliate Program
                </h2>
                <p className="text-neutral-400 mb-6">
                  Enable affiliates to promote your products and earn commissions. 
                  You set the commission rate and affiliates get unique links to share.
                </p>
                
                {/* Affiliate Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-neutral-900/50 rounded-xl p-4 text-center">
                    <MousePointer className="w-6 h-6 text-[#00F0FF] mx-auto mb-2" />
                    <p className="text-2xl font-bold">{affiliatePrograms.reduce((sum, p) => sum + (p.total_clicks || 0), 0)}</p>
                    <p className="text-xs text-neutral-400">Total Clicks</p>
                  </div>
                  <div className="bg-neutral-900/50 rounded-xl p-4 text-center">
                    <ShoppingCart className="w-6 h-6 text-white mx-auto mb-2" />
                    <p className="text-2xl font-bold">{affiliatePrograms.reduce((sum, p) => sum + (p.total_conversions || 0), 0)}</p>
                    <p className="text-xs text-neutral-400">Conversions</p>
                  </div>
                  <div className="bg-neutral-900/50 rounded-xl p-4 text-center">
                    <Users className="w-6 h-6 text-[#00F0FF] mx-auto mb-2" />
                    <p className="text-2xl font-bold">{affiliatePrograms.reduce((sum, p) => sum + (p.total_affiliates || 0), 0)}</p>
                    <p className="text-xs text-neutral-400">Active Affiliates</p>
                  </div>
                  <div className="bg-neutral-900/50 rounded-xl p-4 text-center">
                    <DollarSign className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold">${affiliatePrograms.reduce((sum, p) => sum + (p.total_commissions_paid || 0), 0).toFixed(2)}</p>
                    <p className="text-xs text-neutral-400">Commissions Paid</p>
                  </div>
                </div>
              </div>

              {/* Products with Affiliate Settings */}
              <div>
                <h3 className="text-xl font-bold mb-4">Your Products</h3>
                {products.length === 0 ? (
                  <div className="text-center py-12 bg-neutral-900/30 rounded-2xl border border-white/10">
                    <Package className="w-12 h-12 text-neutral-600 mx-auto mb-3" />
                    <p className="text-neutral-400">Add products first to enable affiliate programs</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {products.map(product => {
                      const program = affiliatePrograms.find(p => p.product_id === product.product_id);
                      const isActive = program?.is_active;
                      
                      return (
                        <div 
                          key={product.product_id}
                          className="bg-neutral-900/50 border border-white/10 rounded-xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
                        >
                          <div className="flex items-center gap-4">
                            <ResilientImg 
                              src={product.images[0]?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${product.images[0]}` : product.images[0]}
                              alt={product.name}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                            <div>
                              <h4 className="font-bold">{product.name}</h4>
                              <p className="text-sm text-neutral-400">${product.price}</p>
                              {program && (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${isActive ? 'bg-green-500/20 text-green-400' : 'bg-neutral-700 text-neutral-400'}`}>
                                    {isActive ? 'Active' : 'Inactive'}
                                  </span>
                                  <span className="text-xs text-neutral-500">
                                    {program.commission_rate}% commission • {program.cookie_days} day cookies
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            {program ? (
                              <>
                                <div className="text-right mr-4">
                                  <p className="text-sm text-neutral-400">{program.total_affiliates || 0} affiliates</p>
                                  <p className="text-xs text-neutral-500">{program.total_conversions || 0} sales</p>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedProductForAffiliate(product);
                                    setAffiliateFormData({
                                      commission_rate: program.commission_rate,
                                      cookie_days: program.cookie_days
                                    });
                                    setShowAffiliateModal(true);
                                  }}
                                  className="border-white/10 hover:bg-white/5"
                                >
                                  <Edit2 className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant={isActive ? "destructive" : "default"}
                                  size="sm"
                                  onClick={() => toggleAffiliateProgram(product.product_id, isActive)}
                                  className={isActive ? '' : 'bg-green-600 hover:bg-green-700'}
                                >
                                  {isActive ? 'Disable' : 'Enable'}
                                </Button>
                              </>
                            ) : (
                              <Button
                                onClick={() => {
                                  setSelectedProductForAffiliate(product);
                                  setAffiliateFormData({ commission_rate: 30, cookie_days: 30 });
                                  setShowAffiliateModal(true);
                                }}
                                className="bg-white text-black hover:bg-[#00F0FF]/90 hover:text-black"
                              >
                                <Percent className="w-4 h-4 mr-2" />
                                Enable Affiliates
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pending Payouts Section */}
              <div className="mt-8">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Pending Affiliate Payouts
                </h3>
                {pendingPayouts.length === 0 ? (
                  <div className="text-center py-8 bg-neutral-900/30 rounded-2xl border border-white/10">
                    <DollarSign className="w-10 h-10 text-neutral-600 mx-auto mb-2" />
                    <p className="text-neutral-400">No pending payouts</p>
                    <p className="text-sm text-neutral-500">When affiliates earn commissions, they will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingPayouts.map(payout => (
                      <div 
                        key={payout.affiliate_link_id}
                        className="bg-neutral-900/50 border border-white/10 rounded-xl p-4 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                            <Users className="w-5 h-5 text-green-400" />
                          </div>
                          <div>
                            <p className="font-semibold">{payout.affiliate_name}</p>
                            <p className="text-sm text-neutral-400">{payout.affiliate_email}</p>
                            <p className="text-xs text-neutral-500">
                              Product: {payout.product_name} • {payout.conversions} sales • {payout.commission_rate}% rate
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-400">
                            ${payout.pending_amount.toFixed(2)}
                          </p>
                          <p className="text-xs text-neutral-500 mb-2">
                            Total earned: ${payout.total_earnings.toFixed(2)}
                          </p>
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedPayout(payout);
                              setPayoutAmount(payout.pending_amount.toFixed(2));
                              setShowPayoutModal(true);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            Mark as Paid
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payout History Section */}
              {payoutHistory.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                    Payout History
                  </h3>
                  <div className="space-y-2">
                    {payoutHistory.slice(0, 10).map(payout => (
                      <div 
                        key={payout.payout_id}
                        className="bg-neutral-900/30 border border-white/5 rounded-lg p-3 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <div>
                            <p className="text-sm font-medium">{payout.affiliate_name}</p>
                            <p className="text-xs text-neutral-500">
                              {new Date(payout.paid_at).toLocaleDateString()}
                              {payout.notes && ` • ${payout.notes}`}
                            </p>
                          </div>
                        </div>
                        <p className="font-semibold text-green-400">${payout.amount.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            <CouponManager />
          </TabsContent>

          {/* Storefront Settings Tab */}
          <TabsContent value="storefront">
            <div className="space-y-8">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Storefront Settings
                </h2>
              </div>

              <form onSubmit={handleSaveStorefront} className="space-y-6">
                {/* Store Information */}
                <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <Store className="w-5 h-5 text-[#00F0FF]" />
                    Store Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Store Name *</label>
                      <Input
                        type="text"
                        value={storefrontFormData.store_name}
                        onChange={(e) => setStorefrontFormData({ ...storefrontFormData, store_name: e.target.value })}
                        placeholder="My AI Store"
                        className="bg-neutral-800 border-white/10"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Store URL Slug *</label>
                      <div className="flex">
                        <span className="bg-neutral-700 border border-r-0 border-white/10 rounded-l-lg px-3 flex items-center text-neutral-400 text-sm">
                          /store/
                        </span>
                        <Input
                          type="text"
                          value={storefrontFormData.store_slug}
                          onChange={(e) => setStorefrontFormData({ ...storefrontFormData, store_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                          placeholder="my-store"
                          className="bg-neutral-800 border-white/10 rounded-l-none"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <label className="block text-sm font-semibold mb-2">Store Bio</label>
                    <Textarea
                      value={storefrontFormData.bio}
                      onChange={(e) => setStorefrontFormData({ ...storefrontFormData, bio: e.target.value })}
                      placeholder="Tell customers about your store and what you offer..."
                      className="bg-neutral-800 border-white/10 min-h-[100px]"
                    />
                  </div>
                </div>

                {/* Storefront Avatar */}
                <div
                  className={`bg-neutral-900/50 border rounded-2xl p-6 transition-all ${
                    isDraggingAvatar
                      ? 'border-[#00F0FF] bg-[#00F0FF]/5 ring-2 ring-[#00F0FF]/40'
                      : 'border-white/10'
                  }`}
                  onDragOver={handleAvatarDragOver}
                  onDragEnter={handleAvatarDragOver}
                  onDragLeave={handleAvatarDragLeave}
                  onDrop={handleAvatarDrop}
                  data-testid="storefront-avatar-card"
                >
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-[#00F0FF]" />
                    Storefront Avatar
                  </h3>
                  <p className="text-neutral-400 text-sm mb-6">
                    {isDraggingAvatar ? (
                      <span className="text-[#00F0FF] font-semibold">Drop your image here to upload</span>
                    ) : (
                      <>Upload or drag a square image (JPG, PNG, WEBP, or GIF, max 5MB). If you don't upload one, we'll show an auto-generated avatar with your store's initial.</>
                    )}
                  </p>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                    <CreatorAvatar
                      storeName={storefrontFormData.store_name || creator?.store_name || '?'}
                      avatarUrl={creator?.avatar || null}
                      size="xl"
                      className="rounded-2xl ring-2 ring-white/10 shrink-0"
                      testId="storefront-avatar-preview"
                    />

                    <div className="flex-1 w-full space-y-3">
                      <input
                        ref={avatarFileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleAvatarFilePicked}
                        className="hidden"
                        data-testid="storefront-avatar-file-input"
                      />
                      <div className="flex flex-wrap gap-3">
                        <Button
                          type="button"
                          onClick={() => avatarFileInputRef.current?.click()}
                          disabled={uploadingAvatar}
                          className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full font-bold"
                          data-testid="storefront-avatar-upload-btn"
                        >
                          {uploadingAvatar ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              {creator?.avatar ? 'Replace image' : 'Upload image'}
                            </>
                          )}
                        </Button>
                        {creator?.avatar && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleRemoveAvatar}
                            disabled={uploadingAvatar}
                            className="border-white/20 text-white rounded-full"
                            data-testid="storefront-avatar-remove-btn"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Remove
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-neutral-500">
                        Recommended: 512×512 px or larger. Square crops look best.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Refund Policy - Required Section */}
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-6">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-400" />
                    Refund Policy *
                    <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full ml-2">Required</span>
                  </h3>
                  <p className="text-neutral-400 text-sm mb-4">
                    You must set a refund policy for your store. This will be displayed to buyers on product pages and during checkout.
                  </p>
                  
                  <Textarea
                    value={storefrontFormData.refund_policy}
                    onChange={(e) => setStorefrontFormData({ ...storefrontFormData, refund_policy: e.target.value })}
                    placeholder="Example: All sales are final. Due to the digital nature of our products, we do not offer refunds. However, if you experience technical issues, please contact us within 7 days of purchase and we will work to resolve any problems."
                    className="bg-neutral-800 border-white/10 min-h-[150px]"
                    required
                  />
                  
                  <div className="mt-4 bg-neutral-800/50 rounded-lg p-4">
                    <h4 className="font-semibold text-sm mb-2 text-white">Tips for a good refund policy:</h4>
                    <ul className="text-xs text-neutral-400 space-y-1">
                      <li>• Be clear about whether you offer refunds or not</li>
                      <li>• Specify the timeframe for refund requests (e.g., 7 days, 30 days)</li>
                      <li>• Explain any conditions or exceptions</li>
                      <li>• Provide contact information for support</li>
                    </ul>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={savingStorefront || !storefrontFormData.refund_policy?.trim()}
                    className="bg-[#00FF94] hover:bg-[#00CC77] text-black rounded-full font-bold px-8"
                  >
                    {savingStorefront ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Save Storefront Settings
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <div className="space-y-6 md:space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Subscriptions
                  </h2>
                  <p className="text-neutral-400 mt-1 text-sm md:text-base">
                    Enable recurring payments
                  </p>
                </div>
              </div>

              {/* Subscription Stats */}
              {subscriptionStats && (
                <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
                  <div className="flex md:grid md:grid-cols-4 gap-3 md:gap-4 min-w-max md:min-w-0">
                    <div className="bg-gradient-to-br from-[#00FF94]/20 to-[#00FF94]/5 border border-[#00FF94]/30 rounded-xl p-3 md:p-4 w-[130px] md:w-auto flex-shrink-0 md:flex-shrink">
                      <p className="text-xs md:text-sm text-neutral-400">Active Subs</p>
                      <p className="text-xl md:text-2xl font-bold text-[#00FF94]">
                        {subscriptionStats.active_subscriptions || 0}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 border border-[#00F0FF]/30 rounded-xl p-3 md:p-4 w-[130px] md:w-auto flex-shrink-0 md:flex-shrink">
                      <p className="text-xs md:text-sm text-neutral-400">Monthly</p>
                      <p className="text-xl md:text-2xl font-bold text-[#00F0FF]">
                        ${subscriptionStats.monthly_revenue?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-white/20 to-white/5 border border-white/30 rounded-xl p-3 md:p-4 w-[130px] md:w-auto flex-shrink-0 md:flex-shrink">
                      <p className="text-xs md:text-sm text-neutral-400">Annual</p>
                      <p className="text-xl md:text-2xl font-bold text-white">
                        ${subscriptionStats.yearly_revenue?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 border border-[#00F0FF]/30 rounded-xl p-3 md:p-4 w-[130px] md:w-auto flex-shrink-0 md:flex-shrink">
                      <p className="text-xs md:text-sm text-neutral-400">Churn</p>
                      <p className="text-xl md:text-2xl font-bold text-[#00F0FF]">
                        {subscriptionStats.churn_rate?.toFixed(1) || '0'}%
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Stripe Connection Check */}
              {!stripeStatus?.charges_enabled && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 md:p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-yellow-500 text-sm md:text-base">Stripe Required</h4>
                    <p className="text-xs md:text-sm text-neutral-400 mt-1">
                      Connect Stripe to enable subscriptions.
                    </p>
                    <Button
                      onClick={handleStripeOnboard}
                      size="sm"
                      className="mt-2 md:mt-3 bg-yellow-500 hover:bg-yellow-600 text-black text-sm"
                    >
                      Connect Stripe
                    </Button>
                  </div>
                </div>
              )}

              {/* Products List for Subscriptions */}
              <div className="space-y-4">
                {products.length === 0 ? (
                  <div className="text-center py-8 md:py-12 bg-neutral-900/50 rounded-xl border border-white/10">
                    <Package className="w-10 h-10 md:w-12 md:h-12 text-neutral-600 mx-auto mb-4" />
                    <p className="text-neutral-400 text-sm md:text-base">No products yet. Create a product first.</p>
                  </div>
                ) : (
                  products.map((product) => (
                    <div
                      key={product.product_id}
                      className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4"
                    >
                      <div className="flex items-center gap-3 md:gap-4">
                        {product.images?.[0] ? (
                          <ResilientImg
                            src={product.images[0].startsWith('http') ? product.images[0] : `${API_URL}${product.images[0]}`}
                            alt={product.name}
                            className="w-12 h-12 md:w-16 md:h-16 object-cover rounded-lg flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 md:w-16 md:h-16 bg-neutral-800 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Package className="w-5 h-5 md:w-6 md:h-6 text-neutral-600" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-sm md:text-base truncate">{product.name}</h4>
                          <p className="text-xs md:text-sm text-neutral-400">
                            ${product.price.toFixed(2)} / one-time
                          </p>
                          {product.is_subscription && (
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#00FF94]/20 border border-[#00FF94]/30 rounded-full text-xs text-[#00FF94]">
                                <RefreshCcw className="w-3 h-3" />
                                Active
                              </span>
                              {product.trial_days > 0 && (
                                <span className="text-xs text-neutral-500">
                                  {product.trial_days}d trial
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-end gap-2 md:gap-3">
                        {product.is_subscription ? (
                          <>
                            <div className="text-right mr-2 md:mr-4 hidden md:block">
                              <p className="text-xs md:text-sm text-neutral-400">Pricing</p>
                              <p className="text-xs md:text-sm">
                                <span className="text-[#00F0FF]">${product.price.toFixed(2)}/mo</span>
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDisableSubscription(product.product_id)}
                              className="border-red-400/30 text-red-400 hover:bg-red-400/10 text-xs md:text-sm px-2 md:px-3"
                            >
                              Disable
                            </Button>
                          </>
                        ) : (
                          <Button
                            onClick={() => openSubscriptionModal(product)}
                            disabled={!stripeStatus?.charges_enabled || enablingSubscription === product.product_id}
                            size="sm"
                            className="bg-[#00FF94] hover:bg-[#00CC75] text-black font-semibold text-xs md:text-sm px-2 md:px-3 whitespace-nowrap"
                          >
                            {enablingSubscription === product.product_id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Enabling...
                              </>
                            ) : (
                              <>
                                <RefreshCcw className="w-4 h-4 mr-2" />
                                Enable Subscription
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Info Section */}
              <div className="bg-[#00FF94]/10 border border-[#00FF94]/30 rounded-xl p-6">
                <h4 className="font-semibold text-[#00FF94] mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  How Subscriptions Work
                </h4>
                <ul className="space-y-2 text-sm text-neutral-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Customers can choose monthly or annual billing when purchasing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Payments are automatically collected by Stripe each billing cycle</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Offer free trials to let customers try before committing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Annual pricing is typically discounted (~17% off) to encourage yearly commitment</span>
                  </li>
                </ul>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="link-analytics">
            <LinkAnalyticsTab products={products} creator={creator} />
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <CustomersTab />
          </TabsContent>

          {/* Sub-Affiliates Tab — 2-tier referral overrides for Founding
              Creators only. Inner component handles eligibility gating +
              opt-in CTA + enrolled dashboard. */}
          <TabsContent value="sub-affiliates">
            <SubAffiliatesTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Affiliate Program Modal */}
      <Dialog open={showAffiliateModal} onOpenChange={setShowAffiliateModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedProductForAffiliate ? `Affiliate Program: ${selectedProductForAffiliate.name}` : 'Create Affiliate Program'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAffiliateProgram} className="space-y-6 mt-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Commission Rate (%)</label>
              <Input
                type="number"
                min="1"
                max="90"
                value={affiliateFormData.commission_rate}
                onChange={(e) => setAffiliateFormData({ ...affiliateFormData, commission_rate: e.target.value })}
                className="bg-neutral-800 border-white/10"
                required
              />
              <p className="text-xs text-neutral-500 mt-1">
                Percentage of each sale paid to affiliates. Industry standard: 30-50%
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Cookie Duration (days)</label>
              <Input
                type="number"
                min="1"
                max="365"
                value={affiliateFormData.cookie_days}
                onChange={(e) => setAffiliateFormData({ ...affiliateFormData, cookie_days: e.target.value })}
                className="bg-neutral-800 border-white/10"
                required
              />
              <p className="text-xs text-neutral-500 mt-1">
                How long after clicking an affiliate link the referral is tracked
              </p>
            </div>

            <div className="bg-white text-black/10 border border-white/30 rounded-xl p-4">
              <h4 className="font-semibold text-sm mb-2">How it works:</h4>
              <ul className="text-xs text-neutral-400 space-y-1">
                <li>• Affiliates sign up and get a unique referral link</li>
                <li>• When someone buys through their link, they earn {affiliateFormData.commission_rate}%</li>
                <li>• Links track for {affiliateFormData.cookie_days} days after click</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                className="flex-1 bg-white text-black hover:bg-[#00F0FF]/90 hover:text-black rounded-full font-bold"
              >
                Save Affiliate Program
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAffiliateModal(false);
                  setSelectedProductForAffiliate(null);
                }}
                className="border-white/10 hover:bg-white/5 rounded-full"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Product Files Modal */}
      <Dialog open={showFilesModal} onOpenChange={(open) => {
        setShowFilesModal(open);
        if (!open) {
          setSelectedProductForFiles(null);
          setProductFiles([]);
        }
      }}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-3">
              <Upload className="w-7 h-7 text-[#00F0FF]" />
              Manage Product Files
            </DialogTitle>
          </DialogHeader>

          {selectedProductForFiles && (
            <div className="space-y-6 mt-4">
              {/* Product Info */}
              <div className="bg-neutral-800/50 rounded-xl p-4">
                <h3 className="font-semibold text-lg mb-1">{selectedProductForFiles.name}</h3>
                <p className="text-base text-neutral-400">${selectedProductForFiles.price}</p>
              </div>

              {/* Delivery method picker (default file = current behaviour) */}
              <DeliveryConfigSection
                product={selectedProductForFiles}
                onUpdated={(updated) => {
                  setSelectedProductForFiles((p) => ({ ...p, ...updated }));
                }}
              />

              {/* Upload Section */}
              <div>
                <label className="block text-base font-semibold mb-3">Upload Delivery File</label>
                <div 
                  className="border-2 border-dashed border-white/20 rounded-xl p-10 text-center hover:border-[#00F0FF]/50 transition-colors cursor-pointer"
                  onClick={() => productFileInputRef.current?.click()}
                >
                  <input
                    ref={productFileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleProductFileUpload}
                    accept=".zip,.pdf,.txt,.md,.json,.csv,.xlsx,.docx,.mp4,.mp3,.png,.jpg,.jpeg"
                  />
                  {uploadingProductFile ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-10 h-10 animate-spin text-[#00F0FF]" />
                      <p className="text-base text-neutral-400">Uploading...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-neutral-500 mx-auto mb-4" />
                      <p className="text-base text-neutral-300 mb-2">Click to upload or drag and drop</p>
                      <p className="text-sm text-neutral-500">ZIP, PDF, MP4, DOCX, and more (max 50MB)</p>
                    </>
                  )}
                </div>
              </div>

              {/* Existing Files */}
              <div>
                <label className="block text-base font-semibold mb-3">
                  Attached Files ({productFiles.length})
                </label>
                {productFiles.length === 0 ? (
                  <div className="bg-neutral-800/30 rounded-xl p-8 text-center">
                    <Package className="w-10 h-10 text-neutral-600 mx-auto mb-3" />
                    <p className="text-base text-neutral-500">No files uploaded yet</p>
                    <p className="text-sm text-neutral-600 mt-2">Upload files that buyers will receive after purchase</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {productFiles.map(file => (
                      <div 
                        key={file.file_id}
                        className="flex items-center justify-between bg-neutral-800/50 rounded-xl p-4"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
                            <Package className="w-6 h-6 text-[#00F0FF]" />
                          </div>
                          <div>
                            <p className="text-base font-medium">{file.original_filename}</p>
                            <p className="text-sm text-neutral-500">{formatFileSize(file.file_size)}</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteProductFile(file.file_id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl p-5">
                <h4 className="font-semibold text-base mb-3 text-[#00F0FF]">How Product Delivery Works</h4>
                <ul className="text-sm text-neutral-300 space-y-2">
                  <li>• Files you upload here are automatically sent to buyers after purchase</li>
                  <li>• Buyers receive download links in their order confirmation email</li>
                  <li>• They can also access downloads from My Purchases anytime</li>
                  <li>• Download links expire after 7 days (10 downloads max per link)</li>
                </ul>
              </div>

              <Button
                onClick={() => {
                  setShowFilesModal(false);
                  setSelectedProductForFiles(null);
                  loadDashboardData(user);
                }}
                className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full font-bold py-3 text-base"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payout Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-green-400" />
              Record Affiliate Payout
            </DialogTitle>
          </DialogHeader>
          
          {selectedPayout && (
            <div className="space-y-4 mt-4">
              <div className="bg-neutral-800/50 rounded-xl p-4">
                <p className="text-sm text-neutral-400">Paying</p>
                <p className="font-semibold text-lg">{selectedPayout.affiliate_name}</p>
                <p className="text-sm text-neutral-500">{selectedPayout.affiliate_email}</p>
              </div>

              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="text-sm text-neutral-400">Pending Amount</p>
                <p className="text-2xl font-bold text-green-400">${selectedPayout.pending_amount.toFixed(2)}</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Amount Paid ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  className="bg-neutral-800 border-white/10"
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Notes (optional)</label>
                <Input
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  className="bg-neutral-800 border-white/10"
                  placeholder="e.g., PayPal transaction ID"
                />
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-sm">
                <p className="text-yellow-400">💡 This records the payout in your dashboard. Make sure to actually send the payment (PayPal, bank transfer, etc.) to the affiliate.</p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleMarkPaid}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full font-bold"
                >
                  Confirm Payout
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPayoutModal(false);
                    setSelectedPayout(null);
                    setPayoutAmount('');
                    setPayoutNotes('');
                  }}
                  className="border-white/10 hover:bg-white/5 rounded-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upsell Modal */}
      <Dialog open={showUpsellModal} onOpenChange={setShowUpsellModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Gift className="w-6 h-6 text-[#FF6B6B]" />
              Create Upsell
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Trigger Product *</label>
              <p className="text-xs text-neutral-500 mb-2">When customer buys this product...</p>
              <select
                value={upsellFormData.product_id}
                onChange={(e) => setUpsellFormData({...upsellFormData, product_id: e.target.value})}
                className="w-full bg-neutral-800 border border-white/10 rounded-lg p-3 text-white"
              >
                <option value="">Select a product</option>
                {products.map(p => (
                  <option key={p.product_id} value={p.product_id}>{p.name} - ${p.price}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Upsell Product *</label>
              <p className="text-xs text-neutral-500 mb-2">...offer them this product</p>
              <select
                value={upsellFormData.upsell_product_id}
                onChange={(e) => setUpsellFormData({...upsellFormData, upsell_product_id: e.target.value})}
                className="w-full bg-neutral-800 border border-white/10 rounded-lg p-3 text-white"
              >
                <option value="">Select a product</option>
                {products.filter(p => p.product_id !== upsellFormData.product_id).map(p => (
                  <option key={p.product_id} value={p.product_id}>{p.name} - ${p.price}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Discount % (Optional)</label>
              <Input
                type="number"
                min="0"
                max="90"
                value={upsellFormData.discount_percent}
                onChange={(e) => setUpsellFormData({...upsellFormData, discount_percent: parseInt(e.target.value) || 0})}
                className="bg-neutral-800 border-white/10"
                placeholder="e.g., 20 for 20% off"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Headline</label>
              <Input
                value={upsellFormData.headline}
                onChange={(e) => setUpsellFormData({...upsellFormData, headline: e.target.value})}
                className="bg-neutral-800 border-white/10"
                placeholder="Special Offer!"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Where to Show</label>
              <select
                value={upsellFormData.position}
                onChange={(e) => setUpsellFormData({...upsellFormData, position: e.target.value})}
                className="w-full bg-neutral-800 border border-white/10 rounded-lg p-3 text-white"
              >
                <option value="checkout">At Checkout</option>
                <option value="cart">In Cart</option>
                <option value="product">On Product Page</option>
              </select>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleCreateUpsell}
                className="flex-1 bg-[#FF6B6B] hover:bg-[#FF5252] text-white rounded-full font-bold"
              >
                Create Upsell
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowUpsellModal(false)}
                className="border-white/10 hover:bg-white/5 rounded-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Modal */}
      <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <RefreshCcw className="w-5 h-5 text-[#00FF94]" />
              Enable Subscription
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {selectedProductForSubscription && (
              <>
                <div className="bg-neutral-800/50 rounded-xl p-4 flex items-center gap-4">
                  {selectedProductForSubscription.images?.[0] ? (
                    <img
                      src={selectedProductForSubscription.images[0].startsWith('http') 
                        ? selectedProductForSubscription.images[0] 
                        : `${API_URL}${selectedProductForSubscription.images[0]}`}
                      alt={selectedProductForSubscription.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-neutral-700 rounded-lg flex items-center justify-center">
                      <Package className="w-6 h-6 text-neutral-500" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold">{selectedProductForSubscription.name}</h4>
                    <p className="text-sm text-neutral-400">
                      Monthly price: ${selectedProductForSubscription.price.toFixed(2)}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Annual Price (USD)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={subscriptionFormData.yearly_price}
                    onChange={(e) => setSubscriptionFormData({ 
                      ...subscriptionFormData, 
                      yearly_price: e.target.value 
                    })}
                    className="bg-neutral-800 border-white/10"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Recommended: ${(selectedProductForSubscription.price * 10).toFixed(2)} (Save ~17% vs monthly)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Free Trial Period (days)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="90"
                    value={subscriptionFormData.trial_days}
                    onChange={(e) => setSubscriptionFormData({ 
                      ...subscriptionFormData, 
                      trial_days: e.target.value 
                    })}
                    className="bg-neutral-800 border-white/10"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Set to 0 for no trial. Max 90 days.
                  </p>
                </div>

                <div className="bg-[#00FF94]/10 border border-[#00FF94]/30 rounded-xl p-4">
                  <h4 className="font-semibold text-sm mb-2 text-[#00FF94]">Pricing Preview</h4>
                  <div className="space-y-1 text-sm">
                    <p>
                      <span className="text-neutral-400">Monthly:</span>{' '}
                      <span className="text-white font-semibold">${selectedProductForSubscription.price.toFixed(2)}/month</span>
                    </p>
                    <p>
                      <span className="text-neutral-400">Annual:</span>{' '}
                      <span className="text-white font-semibold">${parseFloat(subscriptionFormData.yearly_price || 0).toFixed(2)}/year</span>
                      {subscriptionFormData.yearly_price && (
                        <span className="text-green-400 ml-2">
                          ({Math.round((1 - (parseFloat(subscriptionFormData.yearly_price) / (selectedProductForSubscription.price * 12))) * 100)}% savings)
                        </span>
                      )}
                    </p>
                    {subscriptionFormData.trial_days > 0 && (
                      <p className="text-blue-400">
                        + {subscriptionFormData.trial_days} day free trial
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowSubscriptionModal(false);
                      setSelectedProductForSubscription(null);
                    }}
                    className="flex-1 border-white/20"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleEnableSubscription}
                    disabled={enablingSubscription}
                    className="flex-1 bg-[#00FF94] hover:bg-[#00CC75] text-black font-bold"
                  >
                    {enablingSubscription ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enabling...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Enable Subscription
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CreatorDashboard;
